import React, { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Grid
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { format, parse, isValid } from 'date-fns';
import { API_BASE_URL } from '../../../config';

// formData.fecha_inicio / fecha_fin viajan como string 'yyyy-MM-dd' (lo
// que espera el backend); estas funciones convierten esa string a/desde
// el objeto Date que usa el DatePicker.
const stringADate = (value) => {
  if (!value) return null;
  const fecha = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(fecha) ? fecha : null;
};

const dateAString = (date) => {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
};

function NuevaSolicitud({ usuario_id, onSuccess }) {
  const [formData, setFormData] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    comentarios: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [warnings, setWarnings] = useState([]);

  // Días corridos solicitados según las fechas elegidas (mismo cálculo
  // que hace el backend: inclusive de ambas fechas)
  const diasCalculados = (() => {
    if (!formData.fecha_inicio || !formData.fecha_fin) return null;
    const inicio = new Date(formData.fecha_inicio);
    const fin = new Date(formData.fecha_fin);
    if (isNaN(inicio.getTime()) || isNaN(fin.getTime()) || fin < inicio) return null;
    return Math.ceil((fin - inicio) / (1000 * 60 * 60 * 24)) + 1;
  })();
  const requiereRH = diasCalculados !== null && diasCalculados % 7 !== 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar errores y advertencias al cambiar datos
    if (error) setError('');
    if (warnings.length > 0) setWarnings([]);
  };

  const handleFechaChange = (campo) => (date) => {
    setFormData(prev => ({
      ...prev,
      [campo]: dateAString(date)
    }));
    if (error) setError('');
    if (warnings.length > 0) setWarnings([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      console.log('📤 Enviando solicitud con:', {
        fecha_inicio: formData.fecha_inicio,
        fecha_fin: formData.fecha_fin,
        comentarios: formData.comentarios
      });

      const res = await fetch(`${API_BASE_URL}/api/vacaciones/crear-solicitud`, {
        method: 'POST',
        credentials: 'include',  // Incluir cookies automáticamente
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fecha_inicio: formData.fecha_inicio,
          fecha_fin: formData.fecha_fin,
          comentarios: formData.comentarios
        })
      });

      console.log('📥 Respuesta del servidor:', { status: res.status });

      const data = await res.json();

      if (!res.ok) {
        console.log('❌ Error en respuesta:', data);
        if (data.errores && Array.isArray(data.errores)) {
          throw new Error(data.errores.join('. '));
        }
        throw new Error(data.error || 'Error al crear solicitud');
      }

      console.log('✅ Solicitud creada:', data);
      setSuccess(true);
      setWarnings(data.advertencias || []);
      setFormData({ fecha_inicio: '', fecha_fin: '', comentarios: '' });

      // Refrescar datos después de 3 segundos para que se vean las advertencias
      setTimeout(() => {
        onSuccess && onSuccess();
      }, 3000);
    } catch (err) {
      console.error('💥 Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
            ✉️ Crear Nueva Solicitud de Vacaciones
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <div>
                <strong>⚠️ Advertencias:</strong>
                <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                  {warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            </Alert>
          )}

          {success && <Alert severity="success" sx={{ mb: 2 }}>✓ Solicitud creada exitosamente. Pendiente aprobación del referente.</Alert>}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Fecha de Inicio"
                  format="dd/MM/yyyy"
                  value={stringADate(formData.fecha_inicio)}
                  onChange={handleFechaChange('fecha_inicio')}
                  disabled={loading}
                  slotProps={{
                    textField: { fullWidth: true, required: true }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Fecha de Fin"
                  format="dd/MM/yyyy"
                  value={stringADate(formData.fecha_fin)}
                  onChange={handleFechaChange('fecha_fin')}
                  disabled={loading}
                  minDate={stringADate(formData.fecha_inicio) || undefined}
                  slotProps={{
                    textField: { fullWidth: true, required: true }
                  }}
                />
              </Grid>

              {diasCalculados !== null && requiereRH && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Este rango son <strong>{diasCalculados} días</strong>.
                    Esta solicitud va a requerir la aprobación del <strong>referente y de Recursos Humanos</strong>.
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comentarios (opcional)"
                  name="comentarios"
                  value={formData.comentarios}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Motivo de la solicitud (viaje, motivos personales, etc.)"
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                  <Typography variant="body2" color="textSecondary">
                    ℹ️ <strong>Importante:</strong> Antes de cargar la solicitud, validá previamente las fechas con tu jefe directo. Las vacaciones deben solicitarse en bloques de 7 días corridos como mínimo.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading || !formData.fecha_inicio || !formData.fecha_fin}
                  sx={{ py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} /> : '📤 Enviar Solicitud'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
}

export default NuevaSolicitud;
